// Third Party
import moment from "moment";

// Helpers
import { getCellClassNameFn } from "@/Common/helpers/Tables";
import { getDecimalFilterFn, isFooterCell } from "@/Washouts/helpers/tables";
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";
import { accountFormat } from "@/Common/helpers/number";
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { CustomGridTreeDataGroupingCell } from "@/Layouts/components/TreeGrid/CustomGridTreeDataGroupingCell";

// Types
import { Issue } from "@/Common/types/tables";
import { CustomColDef } from "@/Washouts/types/commons";
import { GridCellParams, GridGroupingColDefOverride, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { CustomGridTreeDataGroupingCellProps } from "@/Layouts/components/types";

// Constants
import { ID_GRID_COLDEF } from "@/Washouts/constants";
import { HEADER_CLASS_NAME } from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

const classes = {
	columnHeader: `${HEADER_CLASS_NAME}`,
	columnHeaderEnd: `${HEADER_CLASS_NAME} header-end`,
};

function formatParentValue(value: GridCellParams["value"], isParent = false) {
	if (isParent) {
		return `(${value})`;
	}
	return value;
}

function formatValueIfParent(param: { value: GridCellParams["value"]; row: GridCellParams["row"] }) {
	return formatParentValue(param?.value, Boolean(param?.row?.isParent));
}

function resolveValueFormatter(
	param: { value: GridCellParams["value"]; row: GridCellParams["row"] },
	checks: { autoGeneratedFooter?: string } = { autoGeneratedFooter: "" },
	formatting: { type?: string | null; checkForParent?: boolean; failsCondition?: GridCellParams["value"] } = {
		type: null,
		checkForParent: false,
		failsCondition: null,
	}
) {
	if (checks?.autoGeneratedFooter && isFooterCell(param as GridCellParams)) {
		return checks?.autoGeneratedFooter;
	}
	if (!formatting?.type) {
		return param?.value;
	}
	if (formatting?.checkForParent) {
		return formatValueIfParent(param);
	}
	switch (formatting?.type) {
		case "date":
			return getFormatUTCDate(param?.value as string | number | Date, DATE_FORMAT.usDateDashShortYear);
		case "number": {
			if (isNaN(param?.value as number) && formatting?.failsCondition) {
				return formatting?.failsCondition;
			}
			return +Number(param?.value || 0).toFixed(2);
		}
		default:
			return param?.value;
	}
}

function renderCellWithEmptyFooter(params: GridRenderCellParams) {
	if (isFooterCell(params)) {
		return <TypographyCell params={params}>{""}</TypographyCell>;
	}
	return <TypographyCell params={params} />;
}

export function getColumnDefinitions(issues: Issue[] = []) {
	const cols: CustomColDef[] = [
		ID_GRID_COLDEF,
		{
			field: "repairOrderNo",
			headerName: "RO #",
			width: 70,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} />,
		},
		{
			field: "finishDate",
			headerName: "Date",
			type: "date",
			width: 90,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			valueGetter: (value: Date) => value && getDateInUTC(new Date(moment.utc(value).format(DATE_FORMAT.monthAbbrDate))),
			valueFormatter: (value: Date) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => {
				if (isFooterCell(params)) {
					return null;
				}
				return <TypographyCell params={params} />;
			},
		},
		{
			field: "department",
			headerName: "Department",
			width: 110,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} />,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "operationCode",
			headerName: "Operation Code",
			align: "right",
			width: 120,
			flex: 1,
			resizable: true,
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "startTime",
			headerName: "Start Time",
			width: 79,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} dashed={false} />,
			valueFormatter: (value, row) =>
				resolveValueFormatter({ value, row }, { autoGeneratedFooter: "" }, { type: "number", failsCondition: " " }),
		},
		{
			field: "finishTime",
			headerName: "Finish Time",
			width: 80,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} dashed={false} />,
			valueFormatter: (value, row) =>
				resolveValueFormatter({ value, row }, { autoGeneratedFooter: "" }, { type: "number", failsCondition: " " }),
		},
		{
			field: "reason",
			headerName: "Reason",
			type: "string",
			width: 100,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "actualTime",
			headerName: "Actual Time",
			width: 80,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} dashed={false} render="account" formatOptions={{ decimals: 2 }} />,
			valueFormatter: (value, row) =>
				resolveValueFormatter({ value, row }, { autoGeneratedFooter: "" }, { type: "number", failsCondition: "0.00" }),
		},
		{
			field: "bookedTime",
			headerName: "Booked Time",
			width: 80,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} render="account" formatOptions={{ decimals: 2 }} />,
			valueFormatter: (value, row) =>
				resolveValueFormatter({ value, row }, { autoGeneratedFooter: "" }, { type: "number", failsCondition: "0.00" }),
		},
		{
			field: "bonusTime",
			headerName: "Bonus Time",
			width: 80,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} render="account" formatOptions={{ decimals: 2 }} />,
			valueFormatter: (value, row) =>
				resolveValueFormatter({ value, row }, { autoGeneratedFooter: "" }, { type: "number", failsCondition: "0.00" }),
		},
		{
			field: "jobStatus",
			headerName: "ST",
			width: 80,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "rate",
			headerName: "Rate",
			type: "number",
			width: 100,
			align: "right",
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} left />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => {
				let content;
				const isMoney = !params?.row?.isParent;
				if (params?.row?.isParent) {
					content = `(${+params?.value})`;
				} else {
					content = params?.value ? accountFormat(params?.value, 2) : "-";
				}
				return (
					<TypographyCell params={params} hints={{ isMoney }}>
						{content}
					</TypographyCell>
				);
			},
		},
		{
			field: "extended",
			headerName: "Extended",
			type: "number",
			width: 100,
			align: "right",
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} left />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} render="absolute" hints={{ isMoney: true }} />,
			getApplyQuickFilterFn: getDecimalFilterFn(2),
		},
		{
			field: "extendedBonus",
			headerName: "Extended Bonus",
			type: "number",
			width: 100,
			align: "right",
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} left />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => <TypographyCell params={params} render="absolute" hints={{ isMoney: true }} />,
			getApplyQuickFilterFn: getDecimalFilterFn(2),
		},
		{
			field: "job",
			headerName: "Job",
			width: 80,
			align: "right",
			headerClassName: classes?.columnHeaderEnd,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "payrollStatus",
			headerName: "Payroll Status",
			width: 95,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			width: 60,
			headerClassName: classes?.columnHeader,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: renderCellWithEmptyFooter,
			valueFormatter: (value, row) => formatValueIfParent({ value, row }),
		},
	];
	return cols;
}

export function getGroupingColDef(issues: Issue[] = []): GridGroupingColDefOverride {
	return {
		...prepareGroupColDef(),
		cellClassName: getCellClassNameFn(issues, { noWrap: true }),
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return (
					<TypographyCell params={params} hints={{ noWrap: true }}>
						Total Compensation
					</TypographyCell>
				);
			}
			return (
				<CustomGridTreeDataGroupingCell
					{...(params as CustomGridTreeDataGroupingCellProps)}
					className="size-[20px] relative -left-[3px]"
				/>
			);
		},
	};
}
